import React from 'react';
import {
    Checkbox, Drawer, FormControlLabel, Tooltip
} from '@material-ui/core/';

import EnhancedTable from "@apricityhealth/web-common-lib/components/EnhancedTable";
import Plan from '@apricityhealth/web-common-lib/components/Plan';
import User from '@apricityhealth/web-common-lib/components/User';
import FeatureModelView from './MLModelView';

import Config from '@apricityhealth/web-common-lib/Config';
import Axios from '@apricityhealth/web-common-lib/utils/Axios';
import PubSub from 'pubsub-js'
import moment from 'moment';

export class FeatureModelsView extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            dependencies: true,
            filter: '',
            models: [],
            // Administrators only — everyone else is scoped to their own models by the service regardless,
            // so the checkbox would be a control that does nothing.
            isAdmin: false,
            showAll: false
        }
    }
    
    componentDidMount() {
        this.loadIsAdmin();
        this.token = PubSub.subscribe('PLAN_TOPIC', (msg, data) => {
            if (data.action === 'PlanSelected') {
                if ( this._table ) {
                    this._table.loadData();
                }
            }
        });
    }

    componentWillUnmount(){
        PubSub.unsubscribe(this.token);
    }

    /**
     * Is the caller an administrator?
     *
     * From the SERVER — `/ml/shared` returns it alongside the shared folder list — rather than decoded from
     * the token here. The service is what actually enforces `showAll`; a second answer worked out in the
     * client is a second answer that can disagree with it. On failure the checkbox simply stays hidden,
     * which leaves the caller with the default they would have got anyway.
     */
    loadIsAdmin() {
        const { appContext: { state: { idToken } } } = this.props;
        if (!idToken) return;
        Axios({
            url: Config.baseUrl + `${Config.pathPrefix}apricity-forecast/ml/shared`,
            method: 'GET',
            headers: { "Authorization": idToken }
        }).then((response) => {
            this.setState({ isAdmin: !!(response.data && response.data.isAdmin) });
        }).catch((err) => {
            console.warn("loadIsAdmin failed, hiding the Show All option:", err);
        });
    }

    loadContent({ offset, limit, order, orderBy }) {
        return new Promise((resolve, reject) => {
            let { appContext: { state: { idToken, plan: { planId } } } } = this.props;

            // Only sent when it is on: the service ignores it for anyone who is not an administrator, but an
            // absent parameter is clearer in the logs than showAll=false on every read.
            const showAll = this.state.showAll ? '&showAll=true' : '';
            const getFeatureModels = {
                url: Config.baseUrl + `${Config.pathPrefix}apricity-forecast/${planId}/ml/*?offset=${offset}&limit=${limit}&order=${order}&orderBy=${orderBy}${showAll}`,
                method: 'GET',
                headers: { "Authorization": idToken }
            }
            Axios(getFeatureModels).then(({ data: { models, totalModels }}) => {
                resolve({ data: models, dataLength: totalModels });
            }).catch((err) => {
                reject(err);
            });
        })
    }

    onCloseDialog() {
        this.setState({ dialog: null });
        if (this._table) {
            this._table.setState({ selected: [] }, this._table.loadData.bind(this._table));
        }
    }

    onSelectModel(s, t) {
        console.log("selectModel:", s, t);
        if (s.length > 0) {
            const model = this._table.state.data[s[0]]; //this.state.filtered ? this.state.filtered[s[0]] : this.state.models[s[0]];
            this.onOpenModel( model );
        }
        else {
            // nothing selected..
            this.setState({ dialog: null });
        }
    }

    onOpenModel(model) {
        console.log("onOpenModel:", model );
        let dialog = <Drawer variant="persistent" anchor="right" open={true}>
            <FeatureModelView appContext={this.props.appContext} model={model} onClose={this.onCloseDialog.bind(this)} />
        </Drawer>;
        this.setState({ dialog });
    }

    onCreateModel() {
        console.log("onCreateModel");
        let newModel = { 
            // Qualified rather than a bare 'kmeans': a bare name still resolves to system/, but writing it
            // out is what makes the reference readable next to a user's own copy of the same algorithm.
            algorithm: 'system/kmeans', 
            planId: '', 
            name: '', 
            description: '', 
            modelId: '', 
            biomarkers: {
                dataId: '',
                tupleIndex: 0,
                value: ''
            }
        };
        let dialog = <Drawer variant="persistent" anchor="right" open={true} >
            <FeatureModelView appContext={this.props.appContext} model={newModel} onClose={this.onCloseDialog.bind(this)} />
        </Drawer>;
        this.setState({ progress: null, dialog: dialog });
    }

    render() {
        const { dialog } = this.state;
        const columnData = [
            { id: 'name', numeric: false, disabledPadding: false, label: 'Name' },
            { id: 'description', numeric: false, disabledPadding: false, label: 'Description' },
            { id: 'algorithm', numeric: false, disabledPadding: false, label: 'Algorithm' },
            { id: 'jobs', label: 'Jobs', formatValue: (v) => {
                const sorted = Object.keys(v).sort().reduce((p,c) => {
                    p[c] = v[c];
                    return p;
                }, {} );

                let jobs = '';
                for(let k in sorted) {
                    if ( jobs ) {
                        jobs += ", "
                    }
                    jobs += `${k}: ${v[k]}`
                }
                return jobs;
            } },
            { id: 'nextJobRun', label: 'Next Job', formatValue: (v) => {
                if ( v ) {
                    return moment(v).format("YYYY-MM-DD HH:mm");
                }
                return '';
            }},
            { id: 'planId', numeric: false, disabledPadding: false, label: 'Plan ID', formatValue: (v) => {
                return <Plan appContext={this.props.appContext} planId={v} /> } },
            // Who a model belongs to decides who can even see it, so it belongs in the list rather than only
            // inside the editor. Legacy rows hold a bare string instead of an array.
            { id: 'ownerId', label: 'Owners', formatValue: (v) => {
                const owners = Array.isArray(v) ? v.filter(Boolean) : (v ? [v] : []);
                if (!owners.length) return '';
                return owners.map((userId, i) => <span key={userId}>
                    {i > 0 ? ', ' : ''}<User appContext={this.props.appContext} userId={userId} />
                </span>);
            } },
            { id: 'modelId', numeric: false, disabledPadding: false, label: 'Model ID' },
        ];

        return (
            <table width="100%"><tbody>
                <tr>
                    <td colSpan="2">
                        <EnhancedTable
                            rowsPerPage={25}
                            disableMultiSelect={true}
                            onActions={(table, numSelected, actions) => {
                                // Unconditional, unlike the per-selection actions elsewhere: this filters the
                                // whole list, so hiding it as soon as a row is selected would be a control
                                // that vanishes exactly when someone is working with the list.
                                if (!this.state.isAdmin) return;
                                actions.unshift(
                                    <Tooltip key='showAll' title='Administrators only. Off, this lists the models you own — the same view everyone else gets.'>
                                        <FormControlLabel
                                            style={{ marginRight: 8 }}
                                            label='Show all models'
                                            control={<Checkbox checked={this.state.showAll} onChange={(e) => {
                                                // Reload through the table so the page and the total come back
                                                // consistent, rather than re-filtering a page already fetched.
                                                this.setState({ showAll: e.target.checked }, () => {
                                                    if (this._table) this._table.loadData();
                                                });
                                            }} />} />
                                    </Tooltip>
                                );
                            }}
                            onTable={table => this._table = table}
                            onSelected={this.onSelectModel.bind(this)}
                            onAdd={this.onCreateModel.bind(this)}
                            orderBy='name'
                            columnData={columnData}
                            data={this.loadContent.bind(this)}
                            title='ML Models' />
                        {dialog}
                    </td>
                </tr>
            </tbody></table>
        );
    }
}

export default FeatureModelsView;
